import useQueryBuilding from "ApiLayer/Building/queryBuildingModel";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { atom, useRecoilState } from "recoil";
import {
    EMPTY_BASELINE_PLAN,
    EMPTY_BUILDING_MODEL_CARBON_REDUCTION_PLAN,
    findCarbonReductionPlanById,
} from "./usePlansState.helper";

interface PlansState {
    referencedPlanId: string;
    comparedPlanId: string;
}

const plansStateAtom = atom<PlansState>({
    key: "plansState",
    default: {
        referencedPlanId: "",
        comparedPlanId: "",
    },
});

const usePlansState = () => {
    // State & hooks
    const [state, setState] = useRecoilState(plansStateAtom);
    const { referencedPlanId, comparedPlanId } = state;
    const buildingModelUid = useBuildingModelUid();

    const { data: buildingModel, isLoading } = useQueryBuilding(buildingModelUid);

    // Data from API
    const baselinePlan = buildingModel?.baselineCrp || EMPTY_BASELINE_PLAN;

    const buildingModelCarbonReductionPlans = buildingModel?.carbonReductionPlans || [
        EMPTY_BUILDING_MODEL_CARBON_REDUCTION_PLAN,
    ];

    // Logic for deriving & setting state for reference/compared plans
    const referencebuildingModelCarbonReductionPlan =
        buildingModelCarbonReductionPlans.find((plan) => plan.id === state.referencedPlanId) ||
        EMPTY_BUILDING_MODEL_CARBON_REDUCTION_PLAN;

    const comparedbuildingModelCarbonReductionPlan =
        buildingModelCarbonReductionPlans.find((plan) => plan.id === state.comparedPlanId) ||
        EMPTY_BUILDING_MODEL_CARBON_REDUCTION_PLAN;

    // note: currently not deriving plans from buildingModelCarbonReductionPlans because of race issues
    const referencePlan =
        findCarbonReductionPlanById(state.referencedPlanId, buildingModelCarbonReductionPlans) || baselinePlan;

    const comparedPlan =
        findCarbonReductionPlanById(state.comparedPlanId, buildingModelCarbonReductionPlans) || baselinePlan;

    const comparingToBaseline = comparedPlan === baselinePlan;

    const setReferencedPlanId = (id: string) => {
        setState((prev) => ({
            ...prev,
            referencedPlanId: id,
        }));
    };

    const setComparedPlanId = (id: string) => {
        setState((prev) => ({
            ...prev,
            comparedPlanId: id,
        }));
    };
    return {
        buildingModel,

        baselinePlan,
        buildingModelCarbonReductionPlans,

        // reference plan
        referencePlan,
        referencePlanName: referencebuildingModelCarbonReductionPlan.name,
        referencedPlanId,
        referencebuildingModelCarbonReductionPlan,

        // compared plan
        comparedPlan,
        comparedbuildingModelCarbonReductionPlan,
        comparedPlanName: comparedbuildingModelCarbonReductionPlan.name,
        comparedPlanId,
        comparingToBaseline,

        // actions
        setReferencedPlanId,
        setComparedPlanId,

        // loading state
        isLoading,
    };
};

export default usePlansState;
